/**
 * Display formatters. They GROUP and LABEL; they never compute a money value.
 *
 * Every amount on a transaction card is the decimal string the gateway stored in
 * the signed payload. The one arithmetic this file does is `bundleTotal`, and it
 * is deliberate: the payload has no total field, so the pinned bar's figure is a
 * sum of the payload's OWN step amounts, in integer minor units, taken from the
 * payload and from nothing else (never from chat state, never from a draft).
 * Mixed currencies return `undefined` rather than an invented FX sum.
 */

const MONEY = /^(0|[1-9][0-9]{0,12})\.[0-9]{2}$/;

const SYMBOLS: Readonly<Record<string, string>> = { SGD: "S$", USD: "US$" };

/** A canonical money string ("1674.17"): what `formatMoney` prints — a read that carries anything else is not the contract's. */
export function isMoney(value: string): boolean {
  return MONEY.test(value);
}

export function formatMoney(value: string, currency: string): string {
  if (!MONEY.test(value)) {
    throw new Error(`not a canonical money string: ${JSON.stringify(value)}`);
  }
  const [whole = "0", cents = "00"] = value.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const symbol = SYMBOLS[currency];
  return symbol === undefined ? `${currency} ${grouped}.${cents}` : `${symbol}${grouped}.${cents}`;
}

/**
 * A limit the way the design prints it: "S$5,000", "S$50,000" — whole dollars
 * drop the cents (a limit is a round figure); anything else is printed in full.
 */
export function formatLimit(value: string, currency: string): string {
  const full = formatMoney(value, currency);
  return full.endsWith(".00") ? full.slice(0, -3) : full;
}

/** A canonical money string ("800.00") in integer minor units. */
export function toMinor(value: string): bigint {
  if (!MONEY.test(value)) throw new Error(`not a canonical money string: ${value}`);
  return BigInt(value.replace(".", ""));
}

/** Integer minor units as a canonical money string (a true minus when negative). */
export function fromMinor(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const digits = abs.toString().padStart(3, "0");
  const whole = digits.slice(0, -2);
  const cents = digits.slice(-2);
  return `${negative ? "-" : ""}${whole}.${cents}`;
}

interface AmountLike {
  readonly value: string;
  readonly currency: string;
}
interface StepLike {
  readonly type?: string;
  readonly amount?: AmountLike | undefined;
  readonly cash_amount?: AmountLike | undefined;
}

/** The pinned bar's total, or `undefined` when there is no single honest one. */
/**
 * The header's one figure under a /2 payload: the bank's own per-account totals
 * (`bundle.totals`). One account → its total, as signed, with no sum at all. Several
 * accounts in one currency → their sum (the batch total the signed per-account figures
 * add up to — the only addition left on the card; contract §4.4 signs no grand total).
 * Two currencies → no header total: a sum across currencies is not a figure.
 */
export function signedTotal(totals: readonly { readonly total: { readonly value: string; readonly currency: string } }[]): string | undefined {
  const first = totals[0];
  if (first === undefined) return undefined;
  if (totals.some((t) => t.total.currency !== first.total.currency)) return undefined;
  if (totals.length === 1) return formatMoney(first.total.value, first.total.currency);
  return formatMoney(sumMoney(totals.map((t) => t.total.value)), first.total.currency);
}

export function bundleTotal(steps: readonly StepLike[]): string | undefined {
  let sum = 0n;
  let currency: string | undefined;
  let counted = 0;
  for (const step of steps) {
    const amount = step.amount ?? step.cash_amount;
    if (amount === undefined) continue; // a protective action moves no money
    if (currency === undefined) currency = amount.currency;
    else if (currency !== amount.currency) return undefined;
    sum += toMinor(amount.value);
    counted += 1;
  }
  if (counted === 0 || currency === undefined) return undefined;
  return formatMoney(fromMinor(sum), currency);
}

const TZ = "Asia/Singapore";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface Local {
  readonly day: string; // YYYY-MM-DD in Singapore
  readonly date: string; // "25 Sep"
  readonly hour: number;
  readonly minute: number;
}

function local(iso: string): Local | undefined {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return undefined;
  const f = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: TZ,
  }).formatToParts(at);
  const get = (type: string): string => f.find((p) => p.type === type)?.value ?? "00";
  // Month names are a fixed table, not ICU's: `en-GB` short months moved from
  // "Sep" to "Sept" in a recent ICU, and a bank screen should not change wording
  // with the build it happens to run on.
  return {
    day: `${get("year")}-${get("month")}-${get("day")}`,
    date: `${String(Number(get("day")))} ${MONTHS[Number(get("month")) - 1] ?? get("month")}`,
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
  };
}

const two = (n: number): string => String(n).padStart(2, "0");

/** A clock time, one shape everywhere (DCTA-75 P2-5): 24-hour, zero-padded HH:MM — "09:42", "21:42". */
export function clock(iso: string): string {
  const t = local(iso);
  return t === undefined ? iso : `${two(t.hour)}:${two(t.minute)}`;
}

/** A date and time the way the design prints it: "25 Sep 09:00". Never "in 10 hours". */
export function dateTime(iso: string): string {
  const t = local(iso);
  return t === undefined ? iso : `${t.date} ${two(t.hour)}:${two(t.minute)}`;
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

/** The calendar month an instant falls in, in Singapore: `{ key: "2026-09", name: "September" }`. */
export function monthOf(iso: string): { readonly key: string; readonly name: string } | undefined {
  const t = local(iso);
  if (t === undefined) return undefined;
  const key = t.day.slice(0, 7);
  return { key, name: MONTH_NAMES[Number(key.slice(5, 7)) - 1] ?? key };
}

/** Home's recent rows: "Today", "Yesterday" or "20 Sep". */
export function dayLabel(iso: string, nowMs: number = Date.now()): string {
  const t = local(iso);
  if (t === undefined) return iso;
  const today = local(new Date(nowMs).toISOString());
  const yesterday = local(new Date(nowMs - 86_400_000).toISOString());
  if (t.day === today?.day) return "Today";
  if (t.day === yesterday?.day) return "Yesterday";
  return t.date;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Thu 24 Sep" and the year, in Singapore; the weekday from the calendar date, not ICU. */
function weekdayDate(iso: string): { text: string; year: string } | undefined {
  const t = local(iso);
  if (t === undefined) return undefined;
  const [y, m, d] = t.day.split("-").map(Number) as [number, number, number];
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? "";
  return { text: `${wd} ${t.date}`, year: String(y) };
}

/**
 * An Activity day heading (§8.2): "Today · Thu 24 Sep", "Yesterday · Wed 23 Sep",
 * "Sat 19 Sep" — and "Mon 8 Feb 2021" when the year is not this one.
 */
export function dayHeading(iso: string, nowMs: number = Date.now()): string {
  const w = weekdayDate(iso);
  if (w === undefined) return iso;
  const rel = dayLabel(iso, nowMs);
  if (rel === "Today" || rel === "Yesterday") return `${rel} · ${w.text}`;
  const thisYear = weekdayDate(new Date(nowMs).toISOString())?.year;
  return w.year === thisYear ? w.text : `${w.text} ${w.year}`;
}

const CALENDAR = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A calendar date the bank gives ("2027-09-30"), as printed: "30 Sep 2027" — anything else as it came, never a guess. */
export function calendarDate(date: string): string {
  const m = CALENDAR.exec(date);
  const month = m === null ? undefined : MONTHS[Number(m[2]) - 1];
  return m === null || month === undefined ? date : `${String(Number(m[3]))} ${month} ${m[1] as string}`;
}

/** The month of a calendar date the bank gives ("2022-08-15"): "Aug 2022" (DCTA-176 §F.2 25b, 25j). */
export function calendarMonth(date: string): string {
  const m = CALENDAR.exec(date);
  const month = m === null ? undefined : MONTHS[Number(m[2]) - 1];
  return m === null || month === undefined ? date : `${month} ${m[1] as string}`;
}

/** A calendar date the bank gives ("2026-09-27") with its weekday: "Sun 27 Sep". */
export function calendarWeekday(date: string): string {
  return weekdayDate(`${date}T04:00:00Z`)?.text ?? date;
}

/** The day an ISO instant falls on in Singapore, "YYYY-MM-DD" — a grouping key, never shown. */
export function sgDay(iso: string): string {
  return local(iso)?.day ?? iso;
}

/** A receipt row's time: "Thu 24 Sep 2026, 21:41". */
export function fullWhen(iso: string): string {
  const w = weekdayDate(iso);
  return w === undefined ? iso : `${w.text} ${w.year}, ${clock(iso)}`;
}

/** A receipt sum line's time: "Fri 25 Sep, 09:00". */
export function shortWhen(iso: string): string {
  const w = weekdayDate(iso);
  const t = local(iso);
  return w === undefined || t === undefined ? iso : `${w.text}, ${two(t.hour)}:${two(t.minute)}`;
}

/** The statement's own day column: "22 Sep", never an ISO string. */
export function statementDate(iso: string): string {
  return local(iso)?.date ?? iso;
}

/**
 * "•••• 4417" → "···4417": the registry's own trailing digits, with the
 * design's mark in front. Nothing is re-masked and nothing is un-masked; a
 * string that does not end in digits is returned as it came.
 */
export function lastDigits(masked: string): string {
  const m = /(\d{2,})\s*$/.exec(masked);
  return m === null ? masked : `···${m[1] as string}`;
}

/**
 * A PayNow proxy as the design prints it (DCTA-120, spec §F.2 6c, §F.3): only what the directory left
 * unmasked — "+65 •••• 3321" → "···3321", a UEN's "•••• 345K" → "···345K", a wallet's "•••• @mockwallet"
 * → "···@mockwallet". The end after the directory's last mask, never more; with no mask at all, the
 * last digits (`lastDigits`), as before.
 */
export function proxyTail(masked: string): string {
  const i = masked.lastIndexOf("•");
  if (i === -1) return lastDigits(masked);
  const tail = masked.slice(i + 1).trim();
  return tail === "" ? masked : `···${tail}`;
}

// ------------------------------------------------ the bank's reads, summed
// Home and the success sheet show figures the bank read out. Where the design
// wants a figure no route returns yet (a total, a holding's value), it is
// computed here, exactly, in integer minor units — never on the bank card, which
// prints the signed payload and nothing else. Each one is an ask in README.

/** Decimal strings summed exactly: the total in accounts. */
export function sumMoney(values: readonly string[]): string {
  return fromMinor(values.reduce((n, v) => n + toMinor(v), 0n));
}

function scaled(value: string, places: number): bigint {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (m === null) throw new Error(`not a decimal: ${JSON.stringify(value)}`);
  const frac = (m[3] ?? "").padEnd(places, "0").slice(0, places);
  const n = BigInt(`${m[2] as string}${frac}`);
  return m[1] === "-" ? -n : n;
}

/**
 * units × price, truncated to cents — the bank's own rule (lab/dcta/src/bank/valuation.ts
 * `(unitsScaled * price) / UNIT_SCALE`, and the fill's cost in money.ts `unitsScaledToMinor`),
 * so a holding reads the same here as in the bank's valuation (DCTA-101 run 2: half-up printed
 * S$6,634.40 where the bank said S$6,634.39). Holdings are never negative.
 */
export function holdingValue(units: string, price: string): string {
  const product = scaled(units, 4) * scaled(price, 2); // 6 places
  return fromMinor(product / 10000n);
}

/** Gain since the average cost, one decimal, signed with a true minus. */
export function gainSinceCost(price: string, averageCost: string): string {
  const p = scaled(price, 4);
  const c = scaled(averageCost, 4);
  if (c === 0n) return "0.0%";
  const tenths = ((p - c) * 2000n) / c; // per-mille × 2, for half-up
  const rounded = tenths >= 0n ? (tenths + 1n) / 2n : (tenths - 1n) / 2n;
  const abs = rounded < 0n ? -rounded : rounded;
  const text = `${(abs / 10n).toString()}.${(abs % 10n).toString()}%`;
  return rounded > 0n ? `+${text}` : rounded < 0n ? `\u2212${text}` : text;
}

/** a − b, exactly, in minor units (a card's available credit: its limit less what is owed). */
export function differenceMoney(a: string, b: string): string {
  return fromMinor(toMinor(a) - toMinor(b));
}

/** The bank's own percentage ("18.33") to one decimal, half-up, signed with a true minus: "+18.3%". */
export function signedPercent(pct: string): string {
  const hundredths = scaled(pct, 2);
  const abs0 = hundredths < 0n ? -hundredths : hundredths;
  const tenths = (abs0 + 5n) / 10n;
  const text = `${(tenths / 10n).toString()}.${(tenths % 10n).toString()}%`;
  if (tenths === 0n) return text;
  return hundredths > 0n ? `+${text}` : `\u2212${text}`;
}

/** A gain or a loss as the bank states it ("1496.12", "-12.00"): "+S$1,496.12", "−S$12.00". */
export function signedAmount(value: string, currency: string): string {
  const negative = value.startsWith("-");
  const abs = negative ? value.slice(1) : value;
  const text = formatMoney(abs, currency);
  if (/^0+\.00$/.test(abs)) return text;
  return `${negative ? "\u2212" : "+"}${text}`;
}

/** part / whole as a percentage with two decimals, half-up — a meter's bar width, never a figure. */
export function shareOf(part: string, whole: string): string {
  const w = toMinor(whole);
  if (w <= 0n) return "0.00%";
  const doubled = (toMinor(part) * 20000n) / w;
  const bp = (doubled + 1n) / 2n;
  return `${(bp / 100n).toString()}.${(bp % 100n).toString().padStart(2, "0")}%`;
}

/** A ledger movement with its direction: "−S$86.40", "+S$6,400.00". */
export function signedMoney(value: string, currency: string, direction: string): string {
  return `${direction === "debit" ? "\u2212" : "+"}${formatMoney(value, currency)}`;
}

/** "120 shares", "1 share", "51.7866 shares" — the ledger's units, trailing zeros dropped. */
export function shareCount(units: string): string {
  const trimmed = units.includes(".") ? units.replace(/0+$/, "").replace(/\.$/, "") : units;
  return `${trimmed} ${trimmed === "1" ? "share" : "shares"}`;
}

export function formatUnits(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toFixed(4);
}

// ------------------------------------------------ DCTA-229: the Invest tab's shapes (spec 10-invest §3)
// The tab prints the bank's strings, formatted and never recomputed: these group, sign and label — none of them does sums.

const DECIMAL = /^(-?)(\d+)(\.\d+)?$/;

/**
 * The bank's percentage ("1.85", "-0.94") as it sent it, its sign first: "+1.85%", "−0.94%" (a true minus), "0.00%".
 * Never rounded: the digits are the bank's. `sign: false` drops the sign (a line whose amount already says up or down).
 * What is not a decimal is no figure — undefined, never a guess.
 */
export function bankPercent(pct: string, o: { readonly sign?: boolean } = {}): string | undefined {
  const m = DECIMAL.exec(pct);
  if (m === null) return undefined;
  const digits = `${m[2] as string}${m[3] ?? ""}`;
  if (o.sign === false || !/[1-9]/.test(digits)) return `${digits}%`;
  return `${m[1] === "-" ? "−" : "+"}${digits}%`;
}

/** Shares as the bank counts them: a whole number when the units end in .0000 ("5 shares"), else all four decimals as sent. */
export function unitsText(units: string): string {
  const whole = /^(\d+)(?:\.0+)?$/.exec(units);
  const shown = whole === null ? units : (whole[1] as string).replace(/^0+(?=\d)/, "");
  return `${shown} ${shown === "1" ? "share" : "shares"}`;
}

/** When something ran, after its verb ("Bought …"): "today, 10:42", "yesterday, 10:42", "2 Oct, 10:42" — Singapore, the bank's now. */
export function whenPast(iso: string, nowMs: number): string {
  if (local(iso) === undefined) return iso;
  const day = dayLabel(iso, nowMs);
  return `${day === "Today" ? "today" : day === "Yesterday" ? "yesterday" : day}, ${clock(iso)}`;
}

/** When something waiting goes: "Today, 21:30", "Tomorrow, 09:00", else "Mon 19 Oct, 09:00" — Singapore, the bank's now. */
export function whenAhead(iso: string, nowMs: number): string {
  const t = local(iso);
  if (t === undefined) return iso;
  if (t.day === local(new Date(nowMs).toISOString())?.day) return `Today, ${clock(iso)}`;
  if (t.day === local(new Date(nowMs + 86_400_000).toISOString())?.day) return `Tomorrow, ${clock(iso)}`;
  return shortWhen(iso);
}
